/**
 * Master Bot - Vendor Dashboard
 * Overview of all bots, revenue, and quick actions
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Navbar from '../../components/Navbar';
import StatsCard from '../../components/StatsCard';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { useTelegram } from '../../hooks/useTelegram';

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
`;

const SectionTitle = styled.h2`
  font-size: var(--font-lg);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
  margin: var(--spacing-lg) 0 var(--spacing-md);
`;

const BotList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const BotCard = styled(Card)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BotInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`;

const BotName = styled.div`
  font-size: var(--font-md);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
`;

const BotUsername = styled.div`
  font-size: var(--font-sm);
  color: var(--text-secondary);
`;

const BotStatus = styled.span<{ active: boolean }>`
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: var(--radius-md);
  font-size: var(--font-sm);
  font-weight: var(--font-medium);
  background: ${({ active }) => (active ? 'rgba(76, 175, 80, 0.2)' : 'rgba(142, 142, 147, 0.2)')};
  color: ${({ active }) => (active ? 'var(--accent-green)' : 'var(--text-secondary)')};
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
`;

const MasterDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { showMainButton, showBackButton, close } = useTelegram();
  const [bots] = useState([
    { id: 1, name: 'My Store Bot', username: '@mystorebot', active: true },
    { id: 2, name: 'Shop Assistant', username: '@shopassistant', active: false },
  ]);

  useEffect(() => {
    showMainButton('Create New Bot', () => navigate('/master/create-bot'));
    showBackButton(() => close());

    return () => {
      // Cleanup handled by hook
    };
  }, [showMainButton, showBackButton, close, navigate]);

  return (
    <Layout header={<Navbar title="Dashboard" subtitle="mini app" />}>
      <DashboardGrid>
        <StatsCard label="Total Bots" value={bots.length} icon="🤖" color="var(--accent-green)" />
        <StatsCard label="Active Bots" value={bots.filter((b) => b.active).length} icon="✅" color="var(--accent-green)" />
        <StatsCard label="Total Revenue" value="$0.00" icon="💰" color="var(--accent-green)" />
        <StatsCard
          label="Referrals"
          value="0"
          icon="👥"
          change={{ value: '0 this month', positive: false }}
        />
      </DashboardGrid>

      <SectionTitle>My Bots</SectionTitle>
      <BotList>
        {bots.length === 0 ? (
          <Card>
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
              No bots yet. Create your first bot to get started!
            </p>
          </Card>
        ) : (
          bots.map((bot) => (
            <BotCard key={bot.id} clickable onClick={() => navigate(`/vendor/${bot.username}/admin/dashboard`)}>
              <BotInfo>
                <BotName>{bot.name}</BotName>
                <BotUsername>{bot.username}</BotUsername>
              </BotInfo>
              <BotStatus active={bot.active}>{bot.active ? 'Active' : 'Inactive'}</BotStatus>
            </BotCard>
          ))
        )}
      </BotList>

      <SectionTitle>Quick Actions</SectionTitle>
      <QuickActions>
        <Button variant="primary" onClick={() => navigate('/master/bots')}>
          🤖 My Bots
        </Button>
        <Button variant="secondary" onClick={() => navigate('/master/subscription')}>
          📈 Subscription
        </Button>
        <Button variant="secondary" onClick={() => navigate('/master/wallet')}>
          💰 Wallet
        </Button>
        <Button variant="secondary" onClick={() => navigate('/master/referrals')}>
          👥 Referrals
        </Button>
      </QuickActions>
    </Layout>
  );
};

export default MasterDashboard;
